import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, CheckCircle2, Info, ChevronDown, ChevronUp,
  User, Search, Clock, Star, Pencil, RotateCcw
} from "lucide-react";
import { matchSupplierFromHistory, getVenueSupplierHistory, saveSupplierToHistory } from "./supplierService";
import { invoiceLog } from "./invoiceLogger";

// ─── Confidence badge ─────────────────────────────────────────────────────────
function ConfBadge({ value }) {
  if (value === undefined || value === null) return null;
  const color = value >= 80 ? "bg-emerald-100 text-emerald-700"
    : value >= 50 ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-700";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${color}`}>
      {value}%
    </span>
  );
}

// ─── Field with confidence indicator ─────────────────────────────────────────
function ConfField({ label, value, onChange, type = "text", confidence, required = false, hint }) {
  const [editing, setEditing] = useState(false);
  const low = confidence !== undefined && confidence < 60;

  return (
    <div className={`space-y-1 ${low ? "relative" : ""}`}>
      <div className="flex items-center justify-between gap-1">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
          {label}
          {required && <span className="text-red-400">*</span>}
        </label>
        {confidence !== undefined && <ConfBadge value={confidence} />}
      </div>
      <div className={`relative ${low ? "ring-1 ring-amber-300 rounded-md" : ""}`}>
        <Input
          type={type}
          value={value ?? ""}
          onChange={e => onChange(type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
          className={`h-8 text-sm ${low ? "border-amber-300 bg-amber-50/30" : ""}`}
        />
        {low && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
          </div>
        )}
      </div>
      {hint && <p className="text-[10px] text-gray-400">{hint}</p>}
    </div>
  );
}

// ─── Supplier selector with history ──────────────────────────────────────────
function SupplierPanel({ supplierName, supplierNif, onChangeName, onChangeNif, venueId, fieldConf }) {
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [matched, setMatched] = useState(null);

  useEffect(() => {
    if (venueId) {
      getVenueSupplierHistory(venueId).then(setHistory);
    }
    if (supplierName) {
      const m = matchSupplierFromHistory(supplierName);
      if (m) {
        invoiceLog.info("supplier", `History match: ${m.supplier.name} (${m.confidence}%)`);
        setMatched(m);
      }
    }
  }, []);

  const applyHistorySupplier = (s) => {
    onChangeName(s.name);
    if (s.nif) onChangeNif(s.nif);
    setShowHistory(false);
    setMatched(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
          <User className="w-4 h-4 text-[#7B2D3F]" /> Proveïdor
        </h4>
        {history.length > 0 && (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-xs text-[#7B2D3F] font-semibold hover:underline"
          >
            <Clock className="w-3 h-3" /> Historial ({history.length})
          </button>
        )}
      </div>

      {/* History suggestion */}
      {matched && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl p-2.5">
          <Star className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-blue-800">Proveïdor reconegut ({matched.confidence}%)</p>
            <p className="text-xs text-blue-600 truncate">{matched.supplier.name}</p>
          </div>
          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => applyHistorySupplier(matched.supplier)}>
            Aplicar
          </Button>
        </div>
      )}

      {/* History dropdown */}
      {showHistory && (
        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 py-2 bg-gray-50 text-xs font-bold text-gray-500 uppercase border-b">
            Proveïdors recents
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
            {history.map((s, i) => (
              <button
                key={`step-${i}`}
                onClick={() => applyHistorySupplier(s)}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 text-left"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800">{s.name}</p>
                  {s.nif && <p className="text-xs text-gray-500">{s.nif}</p>}
                </div>
                <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">{s.count}x</Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        <ConfField label="Nom del proveïdor" value={supplierName} onChange={onChangeName}
          confidence={fieldConf?.supplier_name} required />
        <ConfField label="NIF/CIF" value={supplierNif} onChange={onChangeNif}
          confidence={fieldConf?.supplier_nif}
          hint="Format: A12345678 o 12345678A" />
      </div>
    </div>
  );
}

// ─── Editable product lines table ─────────────────────────────────────────────
function ProductLinesTable({ items, onChange }) {
  const [show, setShow] = useState(true);

  const update = (i, key, val) => {
    const next = [...items];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { description: "", quantity: 1, unit: "u", unit_price: 0, line_total: 0, vat_pct: 21 }]);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setShow(!show)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <span>Línies de producte ({items.length})</span>
        {show ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {show && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["Descripció", "Qttat", "Unitat", "Preu unit.", "Total línia", "% IVA", ""].map(h => (
                    <th key={h} className="px-2 py-2 text-left font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, i) => (
                  <tr key={`item-${i}`} className="hover:bg-gray-50/50">
                    <td className="px-2 py-1.5">
                      <Input value={item.description ?? ""} onChange={e => update(i, "description", e.target.value)} className="h-7 text-xs min-w-[140px]" />
                    </td>
                    <td className="px-2 py-1.5"><Input type="number" value={item.quantity ?? ""} onChange={e => update(i, "quantity", parseFloat(e.target.value))} className="h-7 text-xs w-16" /></td>
                    <td className="px-2 py-1.5"><Input value={item.unit ?? ""} onChange={e => update(i, "unit", e.target.value)} className="h-7 text-xs w-14" /></td>
                    <td className="px-2 py-1.5"><Input type="number" value={item.unit_price ?? ""} onChange={e => update(i, "unit_price", parseFloat(e.target.value))} className="h-7 text-xs w-20" /></td>
                    <td className="px-2 py-1.5"><Input type="number" value={item.line_total ?? ""} onChange={e => update(i, "line_total", parseFloat(e.target.value))} className="h-7 text-xs w-20" /></td>
                    <td className="px-2 py-1.5"><Input type="number" value={item.vat_pct ?? ""} onChange={e => update(i, "vat_pct", parseFloat(e.target.value))} className="h-7 text-xs w-14" /></td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600 transition-colors">
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t">
            <button onClick={add} className="text-xs text-[#7B2D3F] font-semibold hover:underline">+ Afegir línia</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tax lines table ──────────────────────────────────────────────────────────
function TaxLinesTable({ taxLines, onChange }) {
  const [show, setShow] = useState(true);
  const update = (i, key, val) => {
    const next = [...taxLines];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };

  if (!taxLines.length) return null;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setShow(!show)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <span>Línies d'IVA ({taxLines.length} tipus)</span>
        {show ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {show && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Base (€)", "% IVA", "Quota IVA (€)", "% Recàrrec", "Recàrrec (€)"].map(h => (
                  <th key={h} className="px-2 py-2 text-left font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {taxLines.map((tl, i) => (
                <tr key={`tl-${i}`} className="hover:bg-gray-50/50">
                  {["base", "vat_pct", "vat_amount", "surcharge_pct", "surcharge_amount"].map(k => (
                    <td key={k} className="px-2 py-1.5">
                      <Input type="number" value={tl[k] ?? ""} onChange={e => update(i, k, parseFloat(e.target.value) || 0)} className="h-7 text-xs w-24" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function InvoiceOCRReview({ data, processedImageUrl, pages, venueId, onConfirm, onBack }) {
  const [form, setForm] = useState({
    supplier_name:     data.supplier_name     || "",
    supplier_nif:      data.supplier_nif      || "",
    invoice_number:    data.invoice_number    || "",
    invoice_date:      data.invoice_date      || "",
    accounting_date:   data.accounting_date   || "",
    payment_terms:     data.payment_terms     || "",
    subtotal:          data.subtotal          || 0,
    vat_amount:        data.vat_amount        || 0,
    surcharge_amount:  data.surcharge_amount  || 0,
    retention_amount:  data.retention_amount  || 0,
    total:             data.total             || 0,
    notes:             data.notes             || "",
  });

  const [taxLines, setTaxLines] = useState(data.tax_lines || []);
  const [items, setItems]       = useState(data.items || []);
  const [imgPageIdx, setImgPageIdx] = useState(0);

  const fieldConf = data.field_confidence || {};
  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  // VAT validation
  const computedTotal = (form.subtotal || 0) + (form.vat_amount || 0) + (form.surcharge_amount || 0) - (form.retention_amount || 0);
  const totalDiff = Math.abs(computedTotal - (form.total || 0));
  const vatValid = form.total > 0 && totalDiff < 0.06;

  const overallConf = data.ocr_confidence || 0;
  const confColor = overallConf >= 80 ? "text-emerald-700 bg-emerald-100 border-emerald-200"
    : overallConf >= 50 ? "text-amber-700 bg-amber-100 border-amber-200"
    : "text-red-700 bg-red-100 border-red-200";

  const handleConfirm = () => {
    saveSupplierToHistory({ name: form.supplier_name, nif: form.supplier_nif });
    invoiceLog.info("ocr-review", "User confirmed OCR data", { supplier: form.supplier_name, total: form.total, vatValid });
    onConfirm({
      ...form,
      tax_lines: taxLines,
      items,
      vat_valid: vatValid,
      vat_warning: vatValid ? null : `Diferència de ${totalDiff.toFixed(2)}€ entre base+IVA i total`,
      ocr_confidence: overallConf,
    });
  };

  const displayPages = pages || (processedImageUrl ? [{ processedUrl: processedImageUrl }] : []);

  return (
    <div className="space-y-5">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-100">
        <div>
          <h3 className="font-bold text-gray-900 text-lg">Revisió de dades extretes per IA</h3>
          <p className="text-xs text-gray-500">Comprova i corregeix cada camp. Els camps en groc tenen baixa confiança.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${confColor} border text-xs font-bold`}>
            Confiança global: {overallConf}%
          </Badge>
        </div>
      </div>

      {/* Validation status */}
      {!vatValid && form.total > 0 ? (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-700">Discrepància en el total detectada</p>
            <p className="text-xs text-red-600">
              Base + IVA = {computedTotal.toFixed(2)}€ · Total declarat: {form.total.toFixed(2)}€ · Diferència: {totalDiff.toFixed(2)}€
            </p>
          </div>
        </div>
      ) : form.total > 0 && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <p className="text-sm font-semibold text-emerald-700">Totals validats correctament</p>
        </div>
      )}

      {/* Two-column layout: image + fields */}
      <div className="grid lg:grid-cols-5 gap-5">
        {/* Image column */}
        <div className="lg:col-span-2 space-y-2">
          <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-white">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Document escanejat</p>
              {displayPages.length > 1 && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setImgPageIdx(Math.max(0, imgPageIdx - 1))} disabled={imgPageIdx === 0}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronDown className="w-3 h-3 rotate-90" />
                  </button>
                  <span className="text-xs font-medium text-gray-600">{imgPageIdx + 1}/{displayPages.length}</span>
                  <button onClick={() => setImgPageIdx(Math.min(displayPages.length - 1, imgPageIdx + 1))} disabled={imgPageIdx === displayPages.length - 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronDown className="w-3 h-3 -rotate-90" />
                  </button>
                </div>
              )}
            </div>
            <img
              src={displayPages[imgPageIdx]?.processedUrl || processedImageUrl}
              alt="Factura"
              className="w-full object-contain max-h-[420px]"
            />
          </div>
          {data.notes && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-xl p-3">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-700">{data.notes}</p>
            </div>
          )}
        </div>

        {/* Fields column */}
        <div className="lg:col-span-3 space-y-5">
          {/* Supplier */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/30">
            <SupplierPanel
              supplierName={form.supplier_name}
              supplierNif={form.supplier_nif}
              onChangeName={set("supplier_name")}
              onChangeNif={set("supplier_nif")}
              venueId={venueId}
              fieldConf={fieldConf}
            />
          </div>

          {/* Invoice reference */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/30 space-y-3">
            <h4 className="text-sm font-bold text-gray-700">Dades de la factura</h4>
            <div className="grid grid-cols-2 gap-3">
              <ConfField label="Nº Factura" value={form.invoice_number} onChange={set("invoice_number")} confidence={fieldConf.invoice_number} required />
              <ConfField label="Data factura" value={form.invoice_date} onChange={set("invoice_date")} type="date" confidence={fieldConf.invoice_date} required />
              <ConfField label="Data comptable" value={form.accounting_date} onChange={set("accounting_date")} type="date" confidence={fieldConf.accounting_date} />
              <ConfField label="Condicions pagament" value={form.payment_terms} onChange={set("payment_terms")} confidence={fieldConf.payment_terms} />
            </div>
          </div>

          {/* Totals */}
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/30 space-y-3">
            <h4 className="text-sm font-bold text-gray-700">Imports</h4>
            <div className="grid grid-cols-2 gap-3">
              <ConfField label="Base imponible (€)" value={form.subtotal} onChange={set("subtotal")} type="number" confidence={fieldConf.subtotal} />
              <ConfField label="Total IVA (€)" value={form.vat_amount} onChange={set("vat_amount")} type="number" confidence={fieldConf.vat_amount} />
              <ConfField label="Recàrrec equiv. (€)" value={form.surcharge_amount} onChange={set("surcharge_amount")} type="number" confidence={fieldConf.surcharge_amount} />
              <ConfField label="Retenció (€)" value={form.retention_amount} onChange={set("retention_amount")} type="number" confidence={fieldConf.retention_amount} />
              <div className="col-span-2">
                <ConfField label="TOTAL FACTURA (€)" value={form.total} onChange={set("total")} type="number" confidence={fieldConf.total} required />
                {vatValid && form.total > 0 && (
                  <p className="text-xs text-emerald-600 mt-1 font-medium flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Total verificat
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tax lines */}
      <TaxLinesTable taxLines={taxLines} onChange={setTaxLines} />

      {/* Product lines */}
      <ProductLinesTable items={items} onChange={setItems} />

      {/* Actions */}
      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <RotateCcw className="w-4 h-4" /> Tornar
        </Button>
        <Button className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white flex-1" onClick={handleConfirm}>
          Confirmar i continuar → Estoc
        </Button>
      </div>
    </div>
  );
}